import CustomDatepickerSelected from 'components/common/CustomDatepickerSelected';
import CalendarIcon from 'components/common/Icons/CalendarIcon';
import { BACKSPACE_CODE, DELETE_CODE } from 'components/common/func';
import { DESKTOP_MIN_WIDTH } from 'constants';
import {
  FX_ADD_TRANSACTION_ENTER_INFORMATION_DOB_NAME,
  FX_ADD_TRANSACTION_ENTER_INFORMATION_PERSONAL_DETAILS_DOB_FIELD,
} from 'constants/fx';
import { datePicker, formatter } from 'helpers';
import { Fade } from 'helpers/animation';
import useLang from 'hooks/useLang';
import useScrollIntoView from 'hooks/useScrollIntoView';
import { useEffect, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import styled from 'styled-components';

const FormInputDatepickerValidation = ({
  ref,
  information,
  setInformation,
  validation,
  setValidation,
}) => {
  const { t } = useLang();

  const [isShowDatePickerIcon, setShowDatePickerIcon] = useState(true);
  const [isOpenDatePicker, setOpenDatePicker] = useState(false);
  const [datePickerISO, setDatePickerISO] = useState(null);
  const [dateMaskStr, setDateMaskStr] = useState(null);

  const validRef = useRef(null);
  useScrollIntoView(
    validRef,
    validation[FX_ADD_TRANSACTION_ENTER_INFORMATION_DOB_NAME],
    [validation]
  );

  const dateFormat = formatter.formatParse(datePicker.dateFormat);

  useEffect(() => {
    if (information[FX_ADD_TRANSACTION_ENTER_INFORMATION_DOB_NAME]) {
      const dateISO = datePicker.dateUTCToDateISO(
        information[FX_ADD_TRANSACTION_ENTER_INFORMATION_DOB_NAME]
      );
      setDatePickerISO(dateISO);

      const { dateMask } = datePicker.getDateMask(
        '',
        information[FX_ADD_TRANSACTION_ENTER_INFORMATION_DOB_NAME]
      );
      setDateMaskStr(formatter.mask(dateMask, dateFormat));
    }
  }, [information]);

  const handleOnChange = (dateUTC) => {
    let newInfomation = { ...information };
    let newValidation = { ...validation };

    const dateISO = datePicker.dateUTCToDateISO(dateUTC);
    const dateMask = datePicker.dateUTCToDateStr(dateUTC)?.split('/')?.join('');

    setDatePickerISO(dateISO);

    setDateMaskStr(dateMask);

    newInfomation = {
      ...newInfomation,
      [FX_ADD_TRANSACTION_ENTER_INFORMATION_DOB_NAME]: datePicker.getDateUTC(
        dateUTC.getDate(),
        dateUTC.getMonth(),
        dateUTC.getFullYear()
      ),
    };
    setInformation(newInfomation);

    const isOfAgeToUse = datePicker.getOfAgeToUse(dateUTC);
    newValidation = {
      ...newValidation,
      [FX_ADD_TRANSACTION_ENTER_INFORMATION_DOB_NAME]: isOfAgeToUse
        ? ''
        : FX_ADD_TRANSACTION_ENTER_INFORMATION_PERSONAL_DETAILS_DOB_FIELD.msgInvalid,
    };
    setValidation(newValidation);

    setOpenDatePicker(false);
  };

  const handleInputOnChange = (e) => {
    const { value } = e.target;

    if (value?.length > 10) return;

    let newValidation = { ...validation };

    setShowDatePickerIcon(true);

    if (!value) {
      const newInfomation = {
        ...information,
        [FX_ADD_TRANSACTION_ENTER_INFORMATION_DOB_NAME]: '',
      };

      setDateMaskStr('');

      setInformation(newInfomation);

      return;
    }

    const { dateMask, dd, MM, yyyy } = datePicker.getDateMask(value);
    setDateMaskStr(formatter.mask(dateMask, dateFormat));

    newValidation = {
      ...newValidation,
      [FX_ADD_TRANSACTION_ENTER_INFORMATION_DOB_NAME]: '',
    };

    if (value.length === 10) {
      const dateUTC = datePicker.getDateUTC(dd, MM - 1, yyyy);
      const dateISO = datePicker.dateUTCToDateISO(dateUTC);
      const currentYear = new Date(dateISO).getFullYear();

      if (
        datePicker.minYear > currentYear ||
        datePicker.maxYear < currentYear
      ) {
        setShowDatePickerIcon(false);

        setDatePickerISO(null);
      } else {
        setShowDatePickerIcon(true);

        setDatePickerISO(dateISO);
      }

      const newInfomation = {
        ...information,
        [FX_ADD_TRANSACTION_ENTER_INFORMATION_DOB_NAME]: dateUTC,
      };
      setInformation(newInfomation);

      const isOfAgeToUse = datePicker.getOfAgeToUse(dateUTC);
      if (!isOfAgeToUse) {
        newValidation = {
          ...newValidation,
          [FX_ADD_TRANSACTION_ENTER_INFORMATION_DOB_NAME]:
            FX_ADD_TRANSACTION_ENTER_INFORMATION_PERSONAL_DETAILS_DOB_FIELD.msgInvalid,
        };
      }
    }

    setValidation(newValidation);
  };

  const handleInputOnKeyDown = (e) => {
    if ([BACKSPACE_CODE, DELETE_CODE].includes(e.which)) {
      const newInfomation = {
        ...information,
        [FX_ADD_TRANSACTION_ENTER_INFORMATION_DOB_NAME]: '',
      };
      setInformation(newInfomation);

      const newValidation = {
        ...validation,
        [FX_ADD_TRANSACTION_ENTER_INFORMATION_DOB_NAME]: '',
      };
      setValidation(newValidation);

      setDateMaskStr('');

      setShowDatePickerIcon(true);
    }
  };

  const handleOnToggleDatePicker = () => {
    setOpenDatePicker(!isOpenDatePicker);
  };

  return (
    <FormGroupStyled>
      <FormLabel>
        {t('form_date_of_birth')}
        <span>*</span>
      </FormLabel>
      <FormControl
        ref={ref}
        type={
          FX_ADD_TRANSACTION_ENTER_INFORMATION_PERSONAL_DETAILS_DOB_FIELD.type
        }
        name={FX_ADD_TRANSACTION_ENTER_INFORMATION_DOB_NAME}
        value={formatter.mask(dateMaskStr, dateFormat)}
        placeholder={
          FX_ADD_TRANSACTION_ENTER_INFORMATION_PERSONAL_DETAILS_DOB_FIELD.placeholder
        }
        onClick={() => setOpenDatePicker(false)}
        onChange={handleInputOnChange}
        onKeyDown={handleInputOnKeyDown}
        isInvalid={validation[FX_ADD_TRANSACTION_ENTER_INFORMATION_DOB_NAME]}
        readOnly={
          FX_ADD_TRANSACTION_ENTER_INFORMATION_PERSONAL_DETAILS_DOB_FIELD.isReadOnly
        }
        inputMode="numeric"
      />
      {isShowDatePickerIcon && (
        <CalendarIcon
          name={FX_ADD_TRANSACTION_ENTER_INFORMATION_DOB_NAME}
          color="var(--ds-c-grey-dark)"
          size={{ width: 24, height: 24 }}
          onClick={handleOnToggleDatePicker}
          styles={{
            position: 'absolute',
            top: '36px',
            right: '10px',
            cursor: 'pointer',
          }}
        />
      )}
      {isOpenDatePicker && (
        <CustomDatepickerSelected
          isOpen={isOpenDatePicker}
          date={datePickerISO}
          onChange={handleOnChange}
          onClose={handleOnToggleDatePicker}
        />
      )}
      <FormControlFeedback ref={validRef} hidden={false} type="invalid">
        {t(validation[FX_ADD_TRANSACTION_ENTER_INFORMATION_DOB_NAME])}
      </FormControlFeedback>
    </FormGroupStyled>
  );
};

const FormGroupStyled = styled(Form.Group)`
  margin-bottom: 32px;
  position: relative;
  ${Fade};

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    margin-bottom: 34px;
  }
`;
const FormLabel = styled(Form.Label)`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;

  display: block;
  color: var(--ds-c-grey-dark);

  span {
    color: var(--c-required);
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;
const FormControl = styled(Form.Control)`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;

  color: var(--ds-c-grey-dark);
  height: 44px;
  border: 1px solid var(--ds-c-grey-light);
  border-radius: 12px;
  padding-right: 8px;
  background: var(--ds-c-white);

  &:focus {
    border: 1px solid var(--ds-c-grey-dark) !important;
    outline: none !important;
    box-shadow: none !important;
  }

  &:invalid,
  &:invalid:focus,
  &.is-invalid {
    border: 1px solid var(--ds-c-red) !important;
    outline: none !important;
    box-shadow: none !important;
    background-image: none !important;
  }

  &[readonly] {
    background: var(--ds-c-white);
  }

  &::placeholder {
    font-family: var(--ff-primary);
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;

    color: var(--ds-c-blue-disabled) !important;

    @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
      font-size: 16px;
      line-height: 20px;
    }
  }

  &[type='date']::-webkit-calendar-picker-indicator {
    background: none !important;
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;
const FormControlFeedback = styled(Form.Control.Feedback)`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;

  color: var(--ds-c-red);
  color: var(--ds-c-red);
  position: absolute;
  margin-top: 4px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 14px;
    line-height: 17px;
  }
`;

export default FormInputDatepickerValidation;
